import { take, put, select, all, takeLatest, actionChannel, takeEvery, Effect, call } from "redux-saga/effects";
import _, { cloneDeep, isEqual } from "lodash";
import {
    CONFIGURATION_CHANGED,
    CONFIGURATION_LOADED,
    ACTIVATE_MASK_ITEM,
    ACTIVATE_ITEM,
    ACTIVATE_BY_ARTICLE_NUMBER,
    SET_MENU_CACHE,
    SET_ACTIVE_MENU_PATH,
    SET_TOTAL_PRICE,
    SET_SUMMARIES,
    SET_SURFACE_PATTERN,
    SET_LAYOUT_GROUPS,
    ACTIVATE_SURFACE_LAYOUTS,
    ACTIVATE_FAVORITE,
    RELOAD_CONFIGURATION,
} from "../../actions/configurationActions";
import { SET_MASK_DATA } from "../../actions/imageActions";
import { CREATE_SESSION, LOAD_CONFIG, SET_IS_LOADING } from "../../actions/sessionActions";
import {
    getSessionId,
    getIconUrl,
    getSelectedMenu,
    getMenuCache,
    getActiveMenuPath,
    getMaskMetadata,
    getPublicOutUrl,
    getProductInfo,
    getProductIds,
    getModelId,
    getCategory,
    getAccount,
    getLayoutGroups,
    getBackendFlavor,
} from "../selectors";

import client from "../../../api/client/SdsWebApiClient";
import { goToModelSelector, goToDefaultPageForReset } from "../../../utilities/navigationUtils";
import { OutBop } from "../../../api/generated/models/OutBop";
import { OutBopUpdate } from "../../../api/generated/models/OutBopUpdate";

import { ItemType } from "../../models/itemType";

import Constants from "../../../constants";
import {
    ActiveMenuItem,
    ApiSurfacePattern,
    ConfigBop,
    ItemMenuPath,
    SdsAccountInfo,
    SdsBackendFlavor,
    SetSurfacePatternResponse,
    SurfaceLayoutGroup,
    ApiTileShape,
} from "../../../api/generated";
import { CONTENT_LOADING, OPEN_SIDE_MENU } from "../../actions/sideMenuActions";
import { ModelMetadata, ProductIdMap, ProductInfoMap } from "../../stateTypes/metadataState";
import { loadMetadataForItems, loadMetadataForModel } from "./metadataSaga";
import { ADD_METADATA } from "../../actions/metadataActions";
import { MenuPathItem, ChildItem, Item, MenuContents, MenuMap, SummaryGroup, SummaryItem } from "../../../models/item";
import { RootState } from "../../store";
import { isMenuCategory } from "../../../utilities/itemUtilities";

const getItemType = (menuId: string | undefined | null) => {
    if (!menuId) throw Error("The menu Id is undefined or null");

    if (/^MENU/i.test(menuId)) return ItemType.Menu;
    if (/^SUBMENU/i.test(menuId)) return ItemType.Submenu;
    if (isMenuCategory(menuId)) return ItemType.Category;

    return ItemType.Item;
};

const selectActiveCategory = (items: Item[]): Item => {
    return items.find(item => item.activeMenuItems && item.activeMenuItems.length > 0) || items[0];
};

/* eslint-disable no-param-reassign */
const getSummariesFromBop = (bop: OutBop): SummaryGroup[] => {
    if (!bop.summaries) return [];

    const groups: SummaryGroup[] = [];
    const existingGroupItems: { [key: string]: SummaryItem[] } = {};
    bop.summaries.forEach(summary => {
        if (summary.menuId) {
            let items = existingGroupItems[summary.menuId];
            if (!items) {
                items = [];
                groups.push({ menuId: summary.menuId, menuText: summary.menuText, items });
                existingGroupItems[summary.menuId] = items;
            }
            items.push({
                id: summary.itemId,
                name: summary.itemText,
                menuText: summary.menuText,
                priceText: summary.itemPrice?.text || undefined,
                articleNumber: summary.articleNumber || undefined,
            });
        }
    });
    return groups;
};

const getActiveMenuItems = (
    update: OutBopUpdate,
    iconUrl: string,
    publicOutUrl: string,
    productInfo: ProductInfoMap,
    productIds: { [key: string]: number },
    metadata: ModelMetadata | null
): ChildItem[] => {
    if (!update.activeMenuItems) return [];
    return update.activeMenuItems.reduce((result: ChildItem[], item: ActiveMenuItem) => {
        const metadataEntry = metadata && metadata[item.id];
        if (metadataEntry?.STATIC_GROUP) {
            return result;
        }

        const info = productInfo.get(item.productId || productIds[item.id]);
        let icon = item.icon || info?.icon;
        icon = icon ? (/^colorpost_icons/.test(icon) ? publicOutUrl : iconUrl) + icon : null;
        if (!icon) {
            icon = metadataEntry?.Icon;
        }
        const shortText = item.shortText || info?.shortText || metadataEntry?.DisplayName;
        result.push({
            id: item.id,
            thumbnail: icon,
            shortText,
            displayName: info?.displayName,
            dynamicType: info?.dynamicType,
            productId: item.productId,
        });
        return result;
    }, []);
};

function getFiltersFromMetadata(entry: { [key: string]: any }) {
    const values: { [key: string]: string } = {};
    Object.entries(entry).forEach(([key, value]) => {
        const match = /^Filter_(.*)/.exec(key);
        if (match) {
            values[match[1]] = value;
        }
    });
    return values;
}

function* getUpdatesFromBop(bop: OutBop | undefined, parentId: string | null): Generator<any, Item[], any> {
    if (!bop?.updates) return [];

    const baseIconUrl = yield select(getIconUrl);
    const basePublicOutUrl = yield select(getPublicOutUrl);
    const iconUrl: string = baseIconUrl ?? basePublicOutUrl ?? "";
    const publicOutUrl: string = basePublicOutUrl ?? "";
    const productInfo: ProductInfoMap = yield select(getProductInfo);
    const productIds: ProductIdMap = yield select(getProductIds);
    const metadata: ModelMetadata = yield select((state: RootState) => state.metadata.metadata[bop.modelId]);

    return bop.updates.reduce((result: Item[], update: OutBopUpdate) => {
        const metadataEntry = metadata && metadata[update.id];
        if (metadataEntry?.STATIC_GROUP) {
            return result;
        }

        const info = productInfo.get(update.productId || productIds[update.id]);
        let icon = update.icon || info?.icon;
        icon = icon ? (/^colorpost_icons/.test(icon) ? publicOutUrl : iconUrl) + icon : null;
        if (!icon) {
            icon = metadataEntry?.Icon;
        }
        const shortText = update.shortText || info?.shortText || metadataEntry?.DisplayName;
        const name = update.name || metadataEntry?.Name;
        const articleNumber = update.articleNumber || info?.retailerId;
        const dynamicType = info?.dynamicType || metadataEntry?.DynamicType || Constants.genericFilterKey;

        result.push({
            id: update.id,
            name,
            shortText,
            parentId,
            isSelected: update.selected,
            price: update.price ? update.price : null,
            thumbnail: icon,
            type: getItemType(update.id),
            productId: update.productId,
            articleNumber,
            dynamicType,
            filterValues: info?.filters || (metadataEntry && getFiltersFromMetadata(metadataEntry)),
            activeMenuItems: getActiveMenuItems(update, iconUrl, publicOutUrl, productInfo, productIds, metadata),
        });

        return result;
    }, []);
}

function* injectMissingMetadata(updates: Item[], modelId: string, menuId: string): any {
    const metadata = yield select(state => state.metadata.metadata[modelId]);
    const missingTextUpdates: any[] = [];
    updates.forEach(update => {
        if (!update.shortText && (!metadata || metadata[update.id]?.DisplayName === undefined)) {
            missingTextUpdates.push(update);
        }
    });

    if (missingTextUpdates.length === 0) return;

    const itemIds = missingTextUpdates.map(u => u.id);

    // If there are too many items without text all the item ids will make the GetItemMetadata request too large.
    // In that case we re-request the bop for the entire menu with full texts instead.
    const idsLength = itemIds.reduce((len, id) => len + id.length + ",item_id=".length, 0);
    if (idsLength > 1500) {
        const sessionId: string = yield select(getSessionId);
        const bop = yield client.activateMenuPost(sessionId, menuId);
        const entries: ModelMetadata = {};
        bop.updates.forEach((update: OutBopUpdate) => {
            const entry: { [key: string]: any } = {};
            // Set shortText (DisplayName) to null if missing to indicate that it should not be fetched again.
            entry.DisplayName = update.shortText || null;
            if (update.name) entry.Name = update.name;
            if (update.information) entry.Description = update.information;
            entries[update.id] = entry;
        });
        yield put({
            type: ADD_METADATA,
            modelId,
            metadata: entries,
        });
    } else {
        const metadataKeys = ["DisplayName", "Name", "Description"];
        yield loadMetadataForItems(itemIds, modelId, metadataKeys, md => {
            itemIds.forEach(id => {
                // Set shortText (DisplayName) to null if missing to indicate that it should not be fetched again.
                const entry = md[id] || {};
                if (!entry.DisplayName) entry.DisplayName = null;
                md[id] = entry;
            });
        });
    }

    const newMetadata = yield select(state => state.metadata.metadata[modelId]);
    missingTextUpdates.forEach(update => {
        const entry = newMetadata[update.id];
        if (entry) {
            update.shortText = update.shortText || entry.DisplayName;
            update.name = update.name || entry.Name;
        }
    });
}

function* getUpdatesFromBopWithMetadata(bop: OutBop | undefined, parentId: string | null): Generator<any, Item[], any> {
    if (!bop?.updates) return [];
    const updates = yield getUpdatesFromBop(bop, parentId);
    if (parentId) {
        yield injectMissingMetadata(updates, bop.modelId, parentId);
    }
    return updates;
}

/* eslint-disable no-param-reassign */
export function* loadConfigurationSaga(
    configString: string,
    encoding?: string,
    surfaceProductArticleNo?: string,
    fromRemoteReload?: boolean
): any {
    try {
        const sessionId: string = yield select(getSessionId);
        if (!sessionId) {
            throw Error("The session has not been created!");
        }

        const configuration: ConfigBop = yield client.loadConfigFromStringPost(
            sessionId,
            configString,
            encoding || "default"
        );

        let { bop } = configuration;
        const { configErrors } = configuration;
        // eslint-disable-next-line no-console
        configErrors.forEach(err => console.log(`Config error: ${err}`));
        let summaries;
        const loadMetadataEffect = loadMetadataForModel(bop.category, bop.modelId);

        // If a surface product was supplied, activate that product as a post-load effect.
        if (surfaceProductArticleNo) {
            const [, activationBop] = yield all([
                loadMetadataEffect,
                client.activateProductsByArticleNoOnAllSurfacesPost(
                    sessionId,
                    surfaceProductArticleNo,
                    undefined,
                    true,
                    true
                ),
            ]);
            bop = activationBop;
            summaries = getSummariesFromBop(activationBop);
        } else {
            const [, summaryResponse] = yield all([loadMetadataEffect, client.getSummaryPost(sessionId)]);
            summaries = getSummariesFromBop(summaryResponse);
        }

        const menus: { [key: string]: MenuContents } = {};
        const items = yield getUpdatesFromBop(bop, null);
        menus[Constants.rootMenuId] = {
            items,
            activePattern: bop.activePattern,
            availablePatterns: bop.availablePatterns || [],
            tileShape: bop.tileShape || ApiTileShape.Rectangular,
        };

        const showMainMenu = menus[Constants.rootMenuId].items.length !== 0;

        yield all([
            put({ type: SET_MENU_CACHE, menus }),
            put({ type: SET_SUMMARIES, summaries }),
            put({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: [], suppressMenuOpen: !!fromRemoteReload }),
            put({
                type: CONFIGURATION_LOADED,
                configurationString: bop.configString,
                modelId: bop.modelId,
                category: bop.category,
                activeSurfaceProductIds: bop.activeSurfaceProductIds,
            }),
            put({ type: SET_TOTAL_PRICE, totalPriceText: bop.totalPriceText, totalPrice: bop.totalPrice }),
        ]);
        yield put({ type: OPEN_SIDE_MENU, sideMenuOpen: fromRemoteReload ? false : showMainMenu });
    } catch (e) {
        console.error("[loadConfigurationSaga]", e);
        // If the session no longer exists on the backend (e.g. expired, server restart), create a new session and load the config instead of redirecting.
        let errorBody: { error?: string } = {};
        const res = e as Response | undefined;
        if (res && typeof res.json === "function") {
            try {
                errorBody = yield call([res, "json"]);
            } catch {
                // ignore
            }
        }
        const isSessionNotFound =
            res?.status === 404 ||
            res?.status === 400 ||
            (typeof errorBody?.error === "string" && errorBody.error.includes("doesn't exist"));

        if (isSessionNotFound) {
            yield put({
                type: CREATE_SESSION,
                initialConfig: configString,
                configEncoding: encoding || undefined,
                surfaceProductArticleNo,
            });
            return;
        }
        yield put({ type: SET_IS_LOADING, isLoading: false });
        // For default-preset entry URLs (start_in_preset=true), reload the entry URL so
        // the preset re-loads. For other URLs, redirect to model selector so the user can
        // try again (e.g. invalid config_string / Base-64 error from backend).
        goToDefaultPageForReset();
    }
}

export function* watchLoadConfigSaga(): any {
    yield takeLatest(LOAD_CONFIG, function* (action: any): any {
        // Remote-control reloads refresh in place: skip the full-page loader and the menu auto-open.
        if (!action.fromRemoteReload) {
            yield put({ type: SET_IS_LOADING, isLoading: true });
        }
        yield call(
            loadConfigurationSaga,
            action.initialConfig,
            action.configEncoding,
            action.surfaceProductArticleNo,
            action.fromRemoteReload
        );
    });
}

function* fetchMenuPath(
    menuPathIds: string[],
    autoActivateCategory: boolean
): Generator<any, { menuCache: MenuMap; menuPath: MenuPathItem[] }, any> {
    const menuCache: MenuMap = {};
    const menuPath: MenuPathItem[] = [];

    const sessionId: string = yield select(getSessionId);
    const rootBop: OutBop = yield client.getRootBopGet(sessionId);
    let items: Item[] = yield getUpdatesFromBop(rootBop, null);
    menuCache[Constants.rootMenuId] = {
        items,
        activePattern: rootBop.activePattern,
        availablePatterns: rootBop.availablePatterns || [],
        tileShape: rootBop.tileShape || ApiTileShape.Rectangular,
    };

    const path = menuPathIds.slice();
    for (let i = 0; i < path.length; i += 1) {
        const menuId = path[i];

        const menuItem = items.find(item => item.id === menuId);
        const breadcrumbText =
            (menuItem && (menuItem.type === ItemType.Menu ? menuItem.name : menuItem.shortText)) || "";
        menuPath.push({ id: menuId, text: breadcrumbText });

        const bop: OutBop = yield client.activateMenuPost(sessionId, menuId);
        items = yield getUpdatesFromBopWithMetadata(bop, menuId);
        menuCache[menuId] = {
            items,
            activePattern: bop.activePattern,
            availablePatterns: bop.availablePatterns || [],
            tileShape: bop.tileShape || ApiTileShape.Rectangular,
        };

        // Activate the first category with an item selected.
        if (
            autoActivateCategory &&
            i + 1 === path.length &&
            items.length > 0 &&
            items.every(item => item.type === ItemType.Category)
        ) {
            const activeCategory = selectActiveCategory(items);
            path.push(activeCategory.id);
        }
    }

    return { menuCache, menuPath };
}

export function* activateMaskItemSaga(): any {
    yield takeLatest(ACTIVATE_MASK_ITEM, function* (action: any): any {
        if (!action.menuPath) return;

        const path: Array<string> = action.menuPath.split("/");
        const { menuCache, menuPath }: { menuCache: MenuMap; menuPath: MenuPathItem[] } = yield fetchMenuPath(
            path,
            true
        );

        yield all([
            put({ type: SET_MENU_CACHE, menus: menuCache }),
            put({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: menuPath }),
        ]);
    });
}

export function* activateMenuSaga(): any {
    yield takeEvery(SET_ACTIVE_MENU_PATH, function* (): any {
        const selectedMenu: string = yield select(getSelectedMenu);
        let menus: MenuMap = yield select(getMenuCache);

        // Update the menu cache with the new menu if missing.
        const menu = menus[selectedMenu];
        let items = menu?.items;
        if (!menu) {
            yield put({
                type: CONTENT_LOADING,
                contentLoading: true,
            });
            const sessionId: string = yield select(getSessionId);
            const bop: OutBop = yield client.activateMenuPost(sessionId, selectedMenu);
            items = yield getUpdatesFromBopWithMetadata(bop, selectedMenu);

            // Fetch again in case another saga modifies something while waiting for response.
            menus = yield select(getMenuCache);
            const updatedMenus = _.cloneDeep(menus);
            updatedMenus[selectedMenu] = {
                items,
                activePattern: bop.activePattern,
                availablePatterns: bop.availablePatterns || [],
                tileShape: bop.tileShape || ApiTileShape.Rectangular,
            };
            yield put({
                type: SET_MENU_CACHE,
                menus: updatedMenus,
            });
        }

        // Auto-select the first category with an item selected in a surfacing menu.
        if (items.length > 0 && items.every(item => item.type === ItemType.Category)) {
            const activeCategory = selectActiveCategory(items);
            const menuPath = yield select(getActiveMenuPath);
            const updatedMenuPath = [...menuPath, { id: activeCategory.id, text: activeCategory.shortText }];
            yield put({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: updatedMenuPath });
        }
    });
}

export function* activateItemSaga(): any {
    // Queue all activations, and process them in sequential order.
    const channel = yield actionChannel(ACTIVATE_ITEM);
    while (true) {
        const action = yield take(channel);
        const itemId: string = action.selectedItem;
        const selectedMenu: string = yield select(getSelectedMenu);
        const menus: MenuMap = yield select(getMenuCache);
        const updatedMenus = cloneDeep(menus);
        const sessionId: string = yield select(getSessionId);
        const modelId = yield select(getModelId);
        const bop: OutBop = yield client.changeConfigItemPost(sessionId, itemId, selectedMenu, true);
        if (modelId !== bop.modelId) {
            yield loadMetadataForModel(bop.category, bop.modelId);
        }
        const items = yield getUpdatesFromBopWithMetadata(bop, selectedMenu);
        updatedMenus[selectedMenu] = {
            items,
            activePattern: bop.activePattern,
            availablePatterns: bop.availablePatterns || [],
            tileShape: bop.tileShape || ApiTileShape.Rectangular,
        };
        const summaries = getSummariesFromBop(bop);
        yield all([
            put({
                type: CONFIGURATION_CHANGED,
                modelId: bop.modelId,
                category: bop.category,
                configurationString: bop.configString,
                activeSurfaceProductIds: bop.activeSurfaceProductIds,
            }),
            put({ type: SET_MENU_CACHE, menus: updatedMenus }),
            put({ type: SET_SUMMARIES, summaries }),
            put({ type: SET_TOTAL_PRICE, totalPriceText: bop.totalPriceText, totalPrice: bop.totalPrice }),
        ]);

        // Activate the correct main menu for classic furniture.
        const activeMenuPath: MenuPathItem[] = yield select(getActiveMenuPath);
        let menuPathIds: string[];
        if (selectedMenu.startsWith("SUBMENU-FURNITURE-MENU")) {
            menuPathIds = [`MENU_${itemId}`, `SUBMENU-FURNITURE-MENU_${itemId}`];
        } else if (bop.hasTypeGroups) {
            menuPathIds = activeMenuPath.map(menu => menu.id);
            // Extract layoutId and combination from itemId
            const [, position, layoutId, combination] = itemId.split("¤");
            menuPathIds = [
                `MENU-FURNITURE${position}-${combination}`,
                `SUBMENU-FurnitureGroup¤${position}¤${layoutId}¤${combination}`,
            ];
        } else if (selectedMenu.startsWith("SUBMENU-LAYOUT-MENU")) {
            yield take(SET_MASK_DATA);
            const metadata = yield select(getMaskMetadata);
            const defaultItemMetadata = _.find(
                metadata,
                md => (md[Constants.menuPathMetadataKey] || "").indexOf(`MENU_${itemId}`) === 0
            );
            const baseMenu = _.first(defaultItemMetadata[Constants.menuPathMetadataKey].split("/")) || "";
            menuPathIds = [`${baseMenu}`, `${selectedMenu}`];
        } else {
            menuPathIds = activeMenuPath.map(menu => menu.id);
        }

        // If we're in the same menu, skip fetching items for that again.
        const isSameMenu = menuPathIds[menuPathIds.length - 1] === selectedMenu;
        if (isSameMenu) {
            menuPathIds.pop();
        }

        // Invalidate/update the menu cache for the menu path in case any
        // submenus have appeared/disappeared or have different texts.
        // All menus outside the path is cleared from the menu cache since they might have changed.
        const { menuCache, menuPath }: { menuCache: MenuMap; menuPath: MenuPathItem[] } = yield fetchMenuPath(
            menuPathIds,
            false
        );

        if (isSameMenu) {
            menuCache[selectedMenu] = updatedMenus[selectedMenu];
            menuPath.push(activeMenuPath[activeMenuPath.length - 1]);
        }

        yield yield all([
            put({ type: SET_MENU_CACHE, menus: menuCache }),
            put({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: menuPath }),
        ]);
    }
}

export function* activateByArticleNumberSaga(): any {
    yield takeLatest(ACTIVATE_BY_ARTICLE_NUMBER, function* (action: any): any {
        if (!action.articleNumber) return;

        // 1. Activate the product in the same menu if it's found.
        const menus: MenuMap = yield select(getMenuCache);
        const selectedMenu: string = yield select(getSelectedMenu);
        const items = menus[selectedMenu]?.items || [];
        const matchingItem = items.find(
            (item: Item) => action.articleNumber.localeCompare(item.articleNumber, "en", { sensitivity: "base" }) === 0
        );

        if (matchingItem) {
            yield put({
                type: ACTIVATE_ITEM,
                selectedItem: matchingItem.id,
            });
            return;
        }

        // Find other surfaces/sets where the product is available.
        const account: SdsAccountInfo = yield select(getAccount);
        const searchParams = new URLSearchParams(window.location.search);
        const setFilter = searchParams.get("settings")?.split(",");
        const paths: ItemMenuPath[] = yield call(
            [client, client.findMenuPathsByArticleNoGet],
            action.articleNumber,
            account.id,
            false,
            setFilter
        );

        const modelId: string = yield select(getModelId);
        const sameModelPaths = paths.filter(p => p.model === modelId);

        // 2. Check if the product is of another product type on the active surface and activate it in that category.
        const activeMenuPath: MenuPathItem[] = yield select(getActiveMenuPath);
        if (activeMenuPath.length > 0 && isMenuCategory(activeMenuPath[activeMenuPath.length - 1].id)) {
            const newMenuPath = activeMenuPath.slice(0, -1);
            const baseMenuIds = newMenuPath.map(p => p.id);
            for (const itemPath of sameModelPaths) {
                if (itemPath.path.length !== activeMenuPath.length) {
                    continue;
                }

                const pathWithoutCategory = itemPath.path.slice(0, itemPath.path.length - 1);
                if (!isEqual(baseMenuIds, pathWithoutCategory)) {
                    continue;
                }

                const newCategoryId = itemPath.path[itemPath.path.length - 1];
                const surfaceMenuId = baseMenuIds[baseMenuIds.length - 1];
                const newCategoryItem = menus[surfaceMenuId].items.find(item => item.id === newCategoryId);
                if (!newCategoryItem) {
                    /* eslint-disable-next-line no-console */
                    console.log(`Could not find category item: ${newCategoryId}`);
                    continue;
                }

                newMenuPath.push({ id: newCategoryItem.id, text: newCategoryItem.shortText });
                yield put({
                    type: ACTIVATE_ITEM,
                    selectedItem: itemPath.itemId,
                });
                yield put({
                    type: SET_ACTIVE_MENU_PATH,
                    activeMenuPath: newMenuPath,
                });
                return;
            }
        }

        // 3. Activate the product on all other surfaces it's available on.
        if (sameModelPaths.length > 0) {
            const sessionId: string = yield select(getSessionId);
            const bop: OutBop = yield call(
                [client, client.activateProductsByArticleNoOnAllSurfacesPost],
                sessionId,
                action.articleNumber,
                selectedMenu !== Constants.rootMenuId ? selectedMenu : undefined,
                true,
                true
            );
            const updatedMenus = cloneDeep(menus);
            if (selectedMenu !== Constants.rootMenuId) {
                updatedMenus[selectedMenu] = {
                    items: yield getUpdatesFromBopWithMetadata(bop, selectedMenu),
                    activePattern: bop.activePattern,
                    availablePatterns: bop.availablePatterns || [],
                    tileShape: bop.tileShape || ApiTileShape.Rectangular,
                };
            }
            const summaries = getSummariesFromBop(bop);
            yield all([
                put({
                    type: CONFIGURATION_CHANGED,
                    modelId: bop.modelId,
                    category: bop.category,
                    configurationString: bop.configString,
                    activeSurfaceProductIds: bop.activeSurfaceProductIds,
                }),
                put({ type: SET_MENU_CACHE, menus: updatedMenus }),
                put({ type: SET_SUMMARIES, summaries }),
                put({ type: SET_TOTAL_PRICE, totalPriceText: bop.totalPriceText, totalPrice: bop.totalPrice }),
            ]);
            return;
        }

        // 4. Activate the product in another room where it's available.
        if (paths.length > 0) {
            // Go through the model selector so a reasonable start config is selected.
            goToModelSelector(action.articleNumber);
        }

        /* eslint-disable-next-line no-console */
        console.log(`Article not found: ${action.articleNumber}`);
    });
}

/**
 * Reloads configuration from the backend (e.g. after SignalR ConfigurationChanged from remote control scan).
 * Fetches current config string and loads it into the existing session so the UI updates.
 */
export function* reloadConfigurationSaga(): any {
    yield takeLatest(RELOAD_CONFIGURATION, function* (): any {
        const sessionId: string = yield select(getSessionId);
        if (!sessionId) {
            console.error("Cannot reload configuration: session not found");
            return;
        }
        console.info("Reloading configuration for session", sessionId);

        try {
            const configString: string = yield call([client, client.getConfigAsStringGet], sessionId, "default");

            if (configString) {
                yield put({
                    type: LOAD_CONFIG,
                    initialConfig: configString,
                    configEncoding: "default",
                    fromRemoteReload: true,
                });
            }
        } catch (error) {
            console.error("Error reloading configuration:", error);
        }
    });
}

export function* setSurfacePattern(): any {
    yield takeEvery(SET_SURFACE_PATTERN, function* (action: any): any {
        const { itemId, pattern, menuId }: { itemId: string; pattern: ApiSurfacePattern; menuId: string } = action;
        const sessionId = yield select(getSessionId);
        const response: SetSurfacePatternResponse = yield client.setSurfacePatternPost(sessionId, itemId, pattern);
        if (response.success) {
            const modelId = yield select(getModelId);
            const category = yield select(getCategory);
            const menus = yield select(getMenuCache);
            const menu = menus[menuId];
            const effects: Effect[] = [
                put({
                    type: CONFIGURATION_CHANGED,
                    modelId,
                    category,
                    configurationString: response.configString,
                }),
            ];
            if (menu) {
                const updatedMenus = cloneDeep(menus);
                updatedMenus[menuId] = {
                    items: menu.items,
                    activePattern: pattern,
                    availablePatterns: menu.availablePatterns,
                    tileShape: menu.tileShape,
                };
                effects.push(put({ type: SET_MENU_CACHE, menus: updatedMenus }));
            }
            yield all(effects);
        }
    });
}

export function* setFavoriteSaga(): any {
    yield takeLatest(ACTIVATE_FAVORITE, function* (action: any): any {
        yield put({
            type: LOAD_CONFIG,
            initialConfig: action.configurationString,
        });
    });
}

export function* updateLayoutsSaga(): any {
    yield takeLatest(CONFIGURATION_LOADED, function* (): any {
        yield put({
            type: SET_LAYOUT_GROUPS,
            layoutGroups: [],
        });
        const backendFlavor = yield select(getBackendFlavor);
        if (backendFlavor === SdsBackendFlavor.SdsSwift) {
            const sessionId: string = yield select(getSessionId);
            const layoutGroups = yield call([client, client.getSurfaceLayoutsGet], sessionId);
            yield put({
                type: SET_LAYOUT_GROUPS,
                layoutGroups,
            });
        }
    });
}

export function* changeSurfaceLayoutsSaga(): any {
    yield takeLatest(ACTIVATE_SURFACE_LAYOUTS, function* (action: any): any {
        const { changedLayouts } = action;
        const sessionId: string = yield select(getSessionId);

        // Make sure we don't leave a menu for an inactive surface open.
        yield put({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: [] });

        // Change all layouts at once.
        const { activeLayouts, bop } = yield call(
            [client, client.setSurfaceLayoutsPost],
            sessionId,
            changedLayouts,
            Constants.rootMenuId,
            true
        );

        // Update main menus, which will change when changing layouts.
        const items = yield getUpdatesFromBop(bop, null);
        const menus: MenuMap = yield select(getMenuCache);
        const updatedMenus = cloneDeep(menus);
        updatedMenus[Constants.rootMenuId] = {
            items,
            activePattern: bop.activePattern,
            availablePatterns: bop.availablePatterns || [],
            tileShape: bop.tileShape || ApiTileShape.Rectangular,
        };

        // Update the active layout in groups that were successfully changed.
        const oldLayoutGroups: SurfaceLayoutGroup[] = yield select(getLayoutGroups);
        const layoutGroups = oldLayoutGroups.map(lg => {
            const activeLayout = activeLayouts[lg.id];
            return activeLayout ? { id: lg.id, activeLayout, layouts: lg.layouts } : lg;
        });

        const summaries = getSummariesFromBop(bop);
        yield all([
            put({ type: SET_MENU_CACHE, menus: updatedMenus }),
            put({
                type: SET_LAYOUT_GROUPS,
                layoutGroups,
            }),
            put({
                type: CONFIGURATION_CHANGED,
                modelId: bop.modelId,
                category: bop.category,
                configurationString: bop.configString,
                activeSurfaceProductIds: bop.activeSurfaceProductIds,
            }),
            put({ type: SET_SUMMARIES, summaries }),
            put({ type: SET_TOTAL_PRICE, totalPriceText: bop.totalPriceText, totalPrice: bop.totalPrice }),
        ]);
    });
}
